import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DicesIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, LightbulbIcon, PencilRulerIcon, ShapesIcon, ShuffleIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://sb.impultienda.ar/storage/v1/object/public/store-resources/d67f4400-71eb-4962-a042-99df60b0b39f/1793411761803_aquagym-caja-6-tomes-transparente-8d14693f.webp';
export const heroGuarantees = ['Téléchargement numérique immédiat', 'Paiement unique', 'Sans abonnement', 'Garantie satisfaction 7 jours'];
export const stats = [{
  value: '240',
  label: 'Pages dans les volumes principaux'
}, {
  value: '6',
  label: "Volumes numériques d'aquagym"
}, {
  value: '3',
  label: 'Groupes – douce, tonique et aquabike'
}, {
  value: '5',
  label: 'Bonus inclus'
}, {
  value: '1',
  label: 'Système complet pour le bassin'
}];
export const differenceStats = [{
  value: '3 groupes',
  label: "De l'aquagym douce à l'aquabike, en trois niveaux"
}, {
  value: '6 volumes numériques',
  label: 'De la programmation de la saison aux badges de progression'
}, {
  value: 'PDF à imprimer',
  label: 'À utiliser sur tablette ou à emporter au bord du bassin'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Les dimanches soir disparaissent',
  text: "Des heures devant l'ordinateur à préparer les séances de la semaine – et ce n'est toujours pas fini."
}, {
  title: 'Des heures à chercher',
  text: "Les exercices trouvés sur internet sont trop simples, trop durs ou pas adaptés à votre groupe."
}, {
  title: 'Chaque séance se construit de zéro',
  text: 'Échauffement, mouvements de base, corps de séance, jeu collectif, retour au calme – ce qui devrait prendre des minutes devient un vrai travail.'
}, {
  title: 'Difficile de gérer des niveaux différents',
  text: "Débutants et habitués dans le même bassin : quelqu'un s'ennuie toujours."
}, {
  title: "Le suivi et l'organisation prennent du temps",
  text: "Fiches de présence, badges, messages aux adhérents : encore du travail après la séance."
}, {
  title: 'Le stress avant la séance',
  text: 'Quand le temps manque, on improvise et on bricole au dernier moment au bord du bassin.'
}];
export const afterItems = ["Un pack d'aquagym organisé pour les adultes de tous âges", 'Des séances minute par minute faciles à suivre', 'Des fiches séance à imprimer tout de suite', "100 exercices d'aquagym et d'aquabike prêts", 'Les mouvements pas à pas et les badges de progression', '3 niveaux dans un même bassin', 'Des règles de sécurité claires au bord du bassin', 'Des fiches de suivi et des repères de progrès prêts', 'Une programmation pour toute la saison'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Les dimanches passent à préparer',
  text: 'Passez-vous une partie du week-end à préparer les séances de la semaine ?'
}, {
  icon: FolderSearchIcon,
  title: 'Vous ne savez plus quel exercice proposer',
  text: 'Cherchez-vous sans cesse des mouvements adaptés au niveau de vos participants ?'
}, {
  icon: LightbulbIcon,
  title: 'Vous voulez des explications plus claires',
  text: 'Avez-vous besoin de supports visuels pour montrer un mouvement simplement depuis la plage ?'
}, {
  icon: PencilRulerIcon,
  title: 'Vous voulez arrêter de tout créer de zéro',
  text: 'Aimeriez-vous que les séances, les exercices et les fiches soient déjà prêts ?'
}, {
  icon: ShuffleIcon,
  title: 'Vos participants ont besoin de variété',
  text: "Voulez-vous plus de chorégraphies, de jeux et d'ateliers pour garder le groupe motivé ?"
}, {
  icon: LayoutGridIcon,
  title: 'Il vous faut une structure plus claire',
  text: "Voulez-vous tout votre matériel réuni pour trouver vite ce qu'il vous faut ?"
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volume 01',
  level: 'Niveaux 1–3',
  title: 'Programmation de la saison & 12 séances',
  tagline: 'Toute la saison organisée, mois par mois.',
  intro: 'Le premier volume donne la programmation de la saison des trois groupes et 12 séances complètes minute par minute.',
  items: ['Programmation aquagym douce et seniors, aquagym tonique, aquabike', 'La semaine à 1, 2 ou 3 créneaux', "Structure d'une séance de 30 et 45 minutes", '3 niveaux dans un même bassin', '12 séances minute par minute', 'Fiches séance à imprimer', 'Plan du bassin et ateliers', 'Sécurité et liste à cocher', 'Fiche de suivi du groupe'],
  outro: 'Arrivez au bord du bassin en sachant exactement quoi faire.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 02',
  level: 'Niveaux 1–3',
  title: 'Banque de séances : 50 séances prêtes',
  tagline: 'Une séance prête pour chaque situation.',
  intro: 'Le deuxième volume réunit 50 séances prêtes, classées par thème et par groupe.',
  items: ['Structures de 30 et 45 minutes', '8 séances aquagym douce et seniors', 'Mobilité et équilibre', 'Cardio doux et rythme', 'Renforcement général avec matériel', 'Aquabike et aquacircuit', 'Avant la journée des badges', 'Séances découverte en grand bain', 'Séances en duo'],
  outro: 'Choisissez, imprimez, animez.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 03',
  level: 'Niveaux 1–3',
  title: "100 exercices d'aquagym et d'aquabike",
  tagline: 'Des participants qui bougent, sourient et reviennent.',
  intro: 'Le troisième volume contient 100 exercices classés par objectif et par groupe, avec variantes et sécurité.',
  items: ["Échauffement dans l'eau", 'Mouvements sans matériel', 'Exercices avec frites et planches', 'Haltères, gants palmés et steps', "Exercices sur l'aquabike", 'Exercices pour la douce et les seniors', 'Jeux collectifs', 'Retour au calme', 'Créer sa propre chorégraphie'],
  outro: "Le plaisir de l'aquagym, sans préparation.",
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 04',
  level: 'Niveaux 1–3',
  title: 'Mouvements pas à pas, musique & badges',
  tagline: 'Chaque mouvement expliqué simplement.',
  intro: 'Le quatrième volume présente les mouvements étape par étape, la musique en BPM et les badges de progression.',
  items: ['La marche aquatique et les déplacements', 'Les mouvements de bras sous l\'eau', "Le réglage et les positions sur l'aquabike", 'La musique et le tempo eau', 'Les chorégraphies', 'Les badges de progression', 'Fiches mouvement à imprimer', 'Erreurs fréquentes et corrections'],
  outro: 'Des mouvements clairs, adaptés à chaque niveau.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 05',
  level: 'Niveaux 1–3',
  title: 'Sécurité, groupes & adhérents',
  tagline: 'Un groupe serein, des adhérents fidèles.',
  intro: 'Le cinquième volume aide à gérer la sécurité au bassin, la vie du groupe et la relation avec les adhérents.',
  items: ['Règles de sécurité au bassin', 'Signes de malaise et que faire', 'Gestion du groupe et rituels', 'Participants inquiets ou débutants', 'Communication avec les adhérents', 'Inscriptions, carte 10 séances et certificat médical', 'Organisation de la saison', 'Listes à cocher'],
  outro: 'Plus de sérénité, séance après séance.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 06',
  level: 'Niveaux 1–3',
  title: 'Progrès, thèmes & fin de saison',
  tagline: 'Voir chaque participant progresser.',
  intro: 'Le sixième volume contient les repères de progrès, les séances à thème et tout pour terminer la saison en beauté.',
  items: ['Fiches de suivi', "Repères d'aisance et de mobilité", "Grilles d'observation", 'Journée des badges', 'Séances à thème', 'Diplômes', 'Bilan de saison', 'Suivi du groupe'],
  outro: 'Des progrès visibles pour les participants et la structure.',
  note: 'Ce document ne remplace pas la formation ni la surveillance réglementaire du bassin (POSS).',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 volumes numériques pour vos groupes',
  text: "Un pack complet d'aquagym et d'aquabike pour préparer et animer vos séances toute la saison. Chaque volume contient 40 pages de séances, d'exercices, de fiches et d'activités pour le bassin."
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Séances prêtes',
  text: "Fini la page blanche. Des séances minute par minute avec les mots de l'instructeur pour démarrer tout de suite."
}, {
  icon: FileTextIcon,
  title: 'Fiches à imprimer',
  text: 'Des fiches séance, des affiches et des fiches de suivi à imprimer et à emporter au bord du bassin.'
}, {
  icon: ShapesIcon,
  title: 'Mouvements en images',
  text: 'Des plans du bassin, des étapes illustrées et des consignes simples pour expliquer chaque mouvement.'
}, {
  icon: DicesIcon,
  title: 'Exercices et jeux',
  text: "100 exercices pour échauffer, faire bouger le groupe et animer avec ou sans matériel, en toute sécurité."
}, {
  icon: ClipboardCheckIcon,
  title: 'Progrès et badges',
  text: "Des grilles d'observation, des repères de progrès et des badges prêts à l'emploi."
}, {
  icon: CalendarCheckIcon,
  title: 'Programmation de la saison',
  text: 'De septembre à juin, chaque mois a son thème, ses objectifs et ses événements.'
}];
export const pageSamples: Array<{src: string; label: string; title: string; text: string}> = [
  {
    "src": "https://sb.impultienda.ar/storage/v1/object/public/store-resources/d67f4400-71eb-4962-a042-99df60b0b39f/1793411841165_aquagym-t1-p01-ed678fa0.webp",
    "label": "Volume 01 · Page 1",
    "title": "La couverture",
    "text": "Programmation de la saison et 12 séances minute par minute."
  },
  {
    "src": "https://sb.impultienda.ar/storage/v1/object/public/store-resources/d67f4400-71eb-4962-a042-99df60b0b39f/1793411847380_aquagym-t1-p04-505718cb.webp",
    "label": "Programmation · Page 4",
    "title": "Aquagym douce et seniors",
    "text": "La programmation de la saison mois par mois, avec les objectifs et la musique en BPM."
  },
  {
    "src": "https://sb.impultienda.ar/storage/v1/object/public/store-resources/d67f4400-71eb-4962-a042-99df60b0b39f/1793411854557_aquagym-t1-p11-f0e06ef9.webp",
    "label": "Fiche séance · Page 11",
    "title": "La fiche séance 1",
    "text": "Le déroulé minute par minute, les 3 niveaux et la sécurité sur une seule page."
  },
  {
    "src": "https://sb.impultienda.ar/storage/v1/object/public/store-resources/d67f4400-71eb-4962-a042-99df60b0b39f/1793411860280_aquagym-t1-p21-6deb6bee.webp",
    "label": "Fiche séance · Page 21",
    "title": "La fiche séance 6",
    "text": "Le matériel, la musique et les conseils de l'instructeur, prêts à imprimer."
  },
  {
    "src": "https://sb.impultienda.ar/storage/v1/object/public/store-resources/d67f4400-71eb-4962-a042-99df60b0b39f/1793411922619_aquagym-t1-p34-4f44bdfc.webp",
    "label": "Ateliers · Page 34",
    "title": 'Plan du bassin et ateliers',
    "text": "Les ateliers dans le même bassin, avec le matériel et le nombre de participants."
  }
];
export const pageTypes = ['Séances prêtes', 'Fiches à imprimer', 'Exercices et ateliers', 'Progrès et badges'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Remplacements sans stress – 12 séances pour le remplaçant',
  text: 'Le groupe continue de bouger même quand vous ne pouvez pas venir.',
  items: ['Fiche de passation', '12 séances prêtes', '4 par groupe', 'Feuille de présence'],
  benefit: 'Partez tranquille, la séance a lieu.'
}, {
  title: "Échauffements en 5 minutes – 60 cartes",
  text: "60 cartes d'aquagym à imprimer pour construire un échauffement en cinq minutes.",
  items: ["Marche et déplacements dans l'eau", 'Mobilité des épaules et des bras', 'Mobilité des hanches et équilibre', '5 échauffements prêts'],
  benefit: 'Un échauffement différent à chaque séance.'
}, {
  title: "Séances à thème et fête de l'aquagym",
  text: "Tout pour organiser des séances à thème et une fête de fin de saison sans pagaille.",
  items: ['Plan sur 6 semaines', '10 séances à thème', 'Diplômes à imprimer', 'Séance « amenez un ami »'],
  benefit: 'Un événement réussi, sans stress.'
}, {
  title: 'Communication avec les adhérents – 40 messages',
  text: 'Des messages prêts pour toute la saison, à copier et envoyer.',
  items: ['Inscriptions et carte 10 séances', 'Rentrée et créneaux', 'Séance annulée ou déplacée', "Réunion d'accueil et FAQ"],
  benefit: 'Des adhérents informés et fidèles.'
}, {
  title: 'Affiches pour la piscine – 15 affiches',
  text: "Des affiches colorées pour organiser la séance et accueillir les participants.",
  items: ['Règles du bassin', "Le réglage de l'aquabike", "Les signaux de l'instructeur", 'Chacun à son rythme'],
  benefit: 'Un bassin qui informe même quand vous parlez.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Ouvrez AQUAGYM PRÊT™',
  text: 'Téléchargez le pack et ouvrez le volume qui correspond à votre groupe.'
}, {
  title: 'Choisissez la séance',
  text: "Trouvez le mois et le thème, et choisissez la séance, l'exercice ou la fiche qui convient à vos participants."
}, {
  title: 'Préparez-vous en 5 minutes',
  text: 'Lisez le déroulé et préparez le matériel et la musique dont vous avez besoin.'
}, {
  title: 'Imprimez et rejoignez le bassin',
  text: 'Imprimez la fiche séance ou gardez-la sur votre tablette – et commencez avec un plan clair.'
}];
export const audienceItems = ["Animez des séances d'aquagym douce, tonique ou d'aquabike", "Voulez toute une saison d'aquagym organisée", 'Voulez arrêter de préparer chaque séance de zéro', 'Avez des niveaux différents dans le même bassin', 'Cherchez des exercices sûrs et variés', 'Voulez des fiches de suivi et des badges prêts', 'Voulez gagner du temps chaque semaine', 'Voulez vous sentir prêt avant chaque séance'];
export const timeValueTasks = ['Chercher des exercices', 'Écrire le déroulé des séances', 'Préparer les ateliers, le matériel et la musique', 'Organiser les groupes et les niveaux', 'Suivre les progrès et écrire aux adhérents'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 volumes numériques – 240 pages',
  detail: 'Programmation, séances, exercices, mouvements, sécurité et suivi'
}, {
  label: '5 bonus sans frais supplémentaires',
  detail: 'Remplacements, échauffements, séances à thème, messages aux adhérents et affiches'
}, {
  label: 'Aquagym douce, tonique et aquabike',
  detail: 'Séances prêtes, fiches à imprimer et suivi des progrès'
}];
export const offerIncludes = ['Téléchargement numérique immédiat', 'Fichiers PDF à conserver', 'Fiches à imprimer', 'Aucun produit physique envoyé'];
export const PRICE = '19,99 €';
export const COMPARE_PRICE = '97 €';

// Only add authentic reviews of AQUAGYM PRÊT. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Comment je reçois le matériel ?',
  a: "AQUAGYM PRÊT™ est livré en numérique. Après l'achat, vous avez accès aux fichiers PDF à télécharger et à conserver. Vous pouvez les utiliser sur ordinateur, tablette ou téléphone et imprimer les fiches dont vous avez besoin."
}, {
  q: 'AQUAGYM PRÊT™ est-il adapté aux piscines françaises ?',
  a: "Oui. Le matériel est pensé pour les maîtres-nageurs (MNS, BPJEPS AAN) et les instructeurs d'aquagym et d'aquabike des piscines municipales, centres aquatiques, clubs et associations : petit bain et grand bain, créneaux, carte 10 séances, inscription trimestrielle, rentrée de septembre et vacances scolaires. Ce document ne remplace pas la formation ni la surveillance réglementaire du bassin (POSS)."
}, {
  q: 'Ça convient à tous mes groupes ?',
  a: "Oui. Les six volumes sont organisés en trois groupes : aquagym douce et seniors en petit bain, aquagym tonique et aquafitness, aquabike et aquacircuit. Vous choisissez selon le public et le niveau de votre groupe."
}, {
  q: 'Je peux vraiment préparer une séance en 5 minutes ?',
  a: "AQUAGYM PRÊT™ est conçu pour accélérer la préparation. Vous ouvrez un volume, choisissez une séance prête et la parcourez en cinq minutes environ. Le temps peut varier selon le groupe et vos adaptations."
}, {
  q: 'Le matériel suffit pour toute une saison ?',
  a: 'AQUAGYM PRÊT™ contient des séances et des activités pour toute la saison, de septembre à juin. Vous choisissez, combinez et adaptez selon votre programmation et votre groupe.'
}, {
  q: 'Il y a des fiches prêtes à imprimer ?',
  a: 'Oui. Le pack contient des fiches séance, des affiches, des fiches de suivi et des diplômes à imprimer.'
}, {
  q: 'Les exercices sont-ils sûrs pour mes participants ?',
  a: "Oui. Entrée dans l'eau par l'escalier, profondeur adaptée, pauses hydratation, version sans saut toujours possible et une règle claire : on augmente l'amplitude et la résistance, jamais la vitesse. Chaque séance a un encadré sécurité."
}, {
  q: "Je peux l'utiliser avec des niveaux différents ?",
  a: 'Oui. Chaque séance a 3 niveaux, pour que débutants et habitués bougent ensemble dans le même bassin.'
}, {
  q: 'Je dois tout imprimer ?',
  a: 'Non. Vous imprimez seulement les pages que vous voulez, et vous pouvez utiliser le reste sur tablette.'
}, {
  q: 'Les cinq bonus sont inclus ?',
  a: `Oui. Les cinq bonus sont inclus dans le pack sans frais supplémentaires – le tout pour ${PRICE}.`
}, {
  q: "C'est un abonnement ?",
  a: 'Non. Vous payez une seule fois et vous avez accès au matériel numérique. Aucun prélèvement récurrent.'
}, {
  q: 'Je peux conserver les fichiers ?',
  a: "Oui. Vous pouvez télécharger et conserver les PDF selon les conditions d'utilisation du produit."
}, {
  q: "Je peux l'utiliser sur téléphone ou tablette ?",
  a: "Oui. Les PDF s'ouvrent sur ordinateur, tablette et téléphone. Pour vérifier la mise en page avant d'imprimer, nous conseillons l'ordinateur ou la tablette."
}, {
  q: 'Y a-t-il une garantie ?',
  a: "Oui. AQUAGYM PRÊT™ a une garantie satisfaction de 7 jours. Si le matériel ne correspond pas à vos attentes, contactez-nous pour demander un remboursement pendant la période de garantie, selon les conditions."
}];
export const footerLinks = ['Conditions de vente', 'Politique de confidentialité', 'Politique de remboursement', 'Contact'];
